const DAY_MS = 24 * 60 * 60 * 1000;

function toISODate(d) {
  if (!d) return null;
  if (typeof d === "string") return d;
  return d.toISOString();
}

/**
 * Estimate a simple production schedule for a ceramics batch.
 * Stages returned here can be fed into PlannerContext tasks in the future.
 *
 * @param {Object} draft - batch/product draft from AdvancedCalculator.
 * @param {Object} opts
 * @param {Date|string} [opts.startDate] - preferred start date; defaults to today.
 * @param {Date|string} [opts.dueDate] - optional due/delivery date.
 * @param {number} [opts.totalHours] - total studio hours from manufactureEstimate.
 * @returns {{ stages: Array<{id:string,label:string,startDate:string,endDate:string}>, warnings: string[] }}
 */
export function estimateProductionSchedule(draft, opts = {}) {
  if (!draft) return { stages: [], warnings: ["No batch data provided."] };

  const now = new Date();
  const start =
    opts.startDate instanceof Date
      ? opts.startDate
      : opts.startDate
      ? new Date(opts.startDate)
      : now;

  const due =
    opts.dueDate instanceof Date
      ? opts.dueDate
      : opts.dueDate
      ? new Date(opts.dueDate)
      : null;

  const totalHours = typeof opts.totalHours === "number" ? opts.totalHours : null;

  let makeDays = 1;
  let trimDays = 1;
  let dryingDays = 2;
  let bisqueDays = 1;
  let glazeDays = 1;
  let glazeFireDays = 1;
  let qcDays = 1;

  if (totalHours && totalHours > 0) {
    const factor = Math.min(3, Math.max(1, totalHours / 3));
    makeDays = Math.max(1, Math.round(1 * factor));
    trimDays = Math.max(1, Math.round(1 * (factor / 1.5)));
    glazeDays = Math.max(1, Math.round(1 * (factor / 1.5)));
  }

  const stages = [];

  const pushStage = (id, label, fromDate, spanDays) => {
    const startDate = new Date(fromDate.getTime());
    const endDate = new Date(fromDate.getTime() + spanDays * DAY_MS);
    stages.push({
      id,
      label,
      startDate: toISODate(startDate),
      endDate: toISODate(endDate),
    });
    return endDate;
  };

  let cursor = start;

  cursor = pushStage("make", "Make / throw", cursor, makeDays);
  cursor = pushStage("trim", "Trim / handle", cursor, trimDays);
  cursor = pushStage("dry", "Drying", cursor, dryingDays);
  cursor = pushStage("bisque", "Bisque firing", cursor, bisqueDays);
  cursor = pushStage("glaze", "Glazing", cursor, glazeDays);
  cursor = pushStage("glazeFire", "Glaze firing", cursor, glazeFireDays);
  cursor = pushStage("qc", "QC / pack", cursor, qcDays);
  const finalEnd = cursor;

  const warnings = [];

  if (due) {
    if (finalEnd.getTime() > due.getTime()) {
      const diffDays = Math.ceil((finalEnd.getTime() - due.getTime()) / DAY_MS);
      warnings.push(
        `This schedule would finish about ${diffDays} day(s) after your due date. Consider starting earlier, reducing scope, or adding another firing.`
      );
    } else {
      const bufferDays = Math.floor((due.getTime() - finalEnd.getTime()) / DAY_MS);
      if (bufferDays < 1) {
        warnings.push("This schedule finishes very close to your due date. There is little buffer for kiln issues or re-firing.");
      } else if (bufferDays < 3) {
        warnings.push(`This schedule leaves about ${bufferDays} day(s) of buffer before your due date.`);
      }
    }
  }

  return { stages, warnings };
}
